import { FC, useId, useMemo } from 'react'

import { clamp } from '../../utilities/clamp'

import { Mask } from './Mask'
import { Star } from './Star'
import { StarRatingProps } from './types'

export const StarRating: FC<StarRatingProps> = ({
  maskId: providedMaskId,
  maxStars = 5,
  stars,
  filledColor = '#111111',
  emptyColor = '#C8C8C8',
  ...props
}) => {
  const generatedMaskId = useId()

  const maskId = providedMaskId ?? generatedMaskId

  const clampedStars = useMemo(
    () => clamp(stars, 0, maxStars),
    [stars, maxStars]
  )

  const maskPercentage = useMemo(() => (clampedStars % 1) * 100, [clampedStars])

  return (
    <div
      className="relative flex items-start [&_svg]:text-[#C8C8C8]"
      role="img"
      aria-label={`${clampedStars} out of ${maxStars} stars`}
      {...props}
    >
      <Mask
        id={maskId}
        aria-hidden="true"
        data-testid="star-mask"
        data-mask-percentage={maskPercentage}
        percentage={maskPercentage}
      />
      {Array(maxStars)
        .fill(0)
        .map((_, idx) => (
          <Star
            key={idx}
            maskId={idx === Math.floor(clampedStars) ? maskId : undefined}
            filledColor={filledColor}
            emptyColor={emptyColor}
            filled={idx <= clampedStars}
            aria-hidden="true"
          />
        ))}
    </div>
  )
}
